import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const FooterContainer = styled.footer`
  background-color: var(--secondary-color);
  color: white;
  padding: 60px 0 20px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 5px;
    background: linear-gradient(to right, var(--accent-color), var(--primary-color));
  }
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 40px;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const FooterColumn = styled.div`
  h3 {
    color: var(--accent-color);
    margin-bottom: 25px;
    font-size: 1.3rem;
    font-weight: 600;
    position: relative;
    padding-bottom: 12px;
    
    &::after {
      content: '';
      position: absolute;
      left: 0;
      bottom: 0;
      width: 50px;
      height: 3px;
      background-color: var(--accent-color);
    }
  }
  
  ul {
    list-style: none;
    
    li {
      margin-bottom: 12px;
      
      a {
        color: rgba(255, 255, 255, 0.8);
        text-decoration: none;
        transition: all 0.3s;
        display: inline-block;
        padding: 3px 0;
        
        &:hover {
          color: var(--accent-color);
          transform: translateX(5px);
        }
      }
    }
  }
`;

const FooterAbout = styled(FooterColumn)`
  p {
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 20px;
    line-height: 1.7;
  }
`;

const FooterLogo = styled.div`
  margin-bottom: 20px;
  
  img {
    max-width: 180px;
    height: auto;
    filter: brightness(0) invert(1);
  }
`;

const FooterSocial = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 20px;
`;

const SocialIcon = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  color: white;
  transition: all 0.3s;
  
  &:hover {
    background-color: var(--accent-color);
    color: var(--secondary-color);
    transform: translateY(-5px);
  }
`;

const FooterContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 15px;
`;

const FooterContactIcon = styled.div`
  margin-right: 10px;
  font-size: 1.2rem;
  color: var(--accent-color);
`;

const FooterContactText = styled.div`
  color: rgba(255, 255, 255, 0.8);
  
  a {
    color: rgba(255, 255, 255, 0.8);
    text-decoration: none;
    transition: color 0.3s;
    
    &:hover {
      color: var(--accent-color);
    }
  }
`;

const Copyright = styled.div`
  text-align: center;
  padding-top: 25px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
`;

const FooterBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const FooterBottomLinks = styled.div`
  display: flex;
  gap: 20px;
  
  @media (max-width: 768px) {
    justify-content: center;
    margin-top: 15px;
  }
  
  a {
    color: rgba(255, 255, 255, 0.6);
    text-decoration: none;
    transition: color 0.3s;
    
    &:hover {
      color: var(--accent-color);
    }
  }
`;

function Footer() {
  return (
    <FooterContainer>
      <div className="container">
        <FooterContent className="footer-content">
          <FooterAbout className="footer-column footer-about">
            <FooterLogo className="footer-logo">
              <img src="/images/logo.png" alt="GuardiansMoves Logo" />
            </FooterLogo>
            <p>
              GuardiansMoves ist Ihr zuverlässiger Partner für professionelle 
              Dienstleistungen in den Bereichen Sicherheit, Taxi & Mietwagenservice und Reinigung in Berlin und Umgebung.
            </p>
            
            <FooterSocial className="footer-social">
             
              <SocialIcon href="https://www.instagram.com/guardiansmoves?igsh=MmE1YjdkcTR4amIy" className="social-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M8 0C5.829 0 5.556.01 4.703.048 3.85.088 3.269.222 2.76.42a3.917 3.917 0 0 0-1.417.923A3.927 3.927 0 0 0 .42 2.76C.222 3.268.087 3.85.048 4.7.01 5.555 0 5.827 0 8.001c0 2.172.01 2.444.048 3.297.04.852.174 1.433.372 1.942.205.526.478.972.923 1.417.444.445.89.719 1.416.923.51.198 1.09.333 1.942.372C5.555 15.99 5.827 16 8 16s2.444-.01 3.298-.048c.851-.04 1.434-.174 1.943-.372a3.916 3.916 0 0 0 1.416-.923c.445-.445.718-.891.923-1.417.197-.509.332-1.09.372-1.942C15.99 10.445 16 10.173 16 8s-.01-2.445-.048-3.299c-.04-.851-.175-1.433-.372-1.941a3.926 3.926 0 0 0-.923-1.417A3.911 3.911 0 0 0 13.24.42c-.51-.198-1.092-.333-1.943-.372C10.443.01 10.172 0 7.998 0h.003zm-.717 1.442h.718c2.136 0 2.389.007 3.232.046.78.035 1.204.166 1.486.275.373.145.64.319.92.599.28.28.453.546.598.92.11.281.24.705.275 1.485.039.843.047 1.096.047 3.231s-.008 2.389-.047 3.232c-.035.78-.166 1.203-.275 1.485a2.47 2.47 0 0 1-.599.919c-.28.28-.546.453-.92.598-.28.11-.704.24-1.485.276-.843.038-1.096.047-3.232.047s-2.39-.009-3.233-.047c-.78-.036-1.203-.166-1.485-.276a2.478 2.478 0 0 1-.92-.598 2.48 2.48 0 0 1-.6-.92c-.109-.281-.24-.705-.275-1.485-.038-.843-.046-1.096-.046-3.233 0-2.136.008-2.388.046-3.231.036-.78.166-1.204.276-1.486.145-.373.319-.64.599-.92.28-.28.546-.453.92-.598.282-.11.705-.24 1.485-.276.738-.034 1.024-.044 2.515-.045v.002zm4.988 1.328a.96.96 0 1 0 0 1.92.96.96 0 0 0 0-1.92zm-4.27 1.122a4.109 4.109 0 1 0 0 8.217 4.109 4.109 0 0 0 0-8.217zm0 1.441a2.667 2.667 0 1 1 0 5.334 2.667 2.667 0 0 1 0-5.334z"/>
                </svg>
              </SocialIcon>
              <SocialIcon href="#" className="social-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708c0 .633-.526 1.146-1.175 1.146H1.175C.526 16 0 15.487 0 14.854V1.146zm4.943 12.248V6.169H2.542v7.225h2.401zm-1.2-8.212c.837 0 1.358-.554 1.358-1.248-.015-.709-.52-1.248-1.342-1.248-.822 0-1.359.54-1.359 1.248 0 .694.521 1.248 1.327 1.248h.016zm4.908 8.212V9.359c0-.216.016-.432.08-.586.173-.431.568-.878 1.232-.878.869 0 1.216.662 1.216 1.634v3.865h2.401V9.25c0-2.22-1.184-3.252-2.764-3.252-1.274 0-1.845.7-2.165 1.193v.025h-.016a5.54 5.54 0 0 1 .016-.025V6.169h-2.4c.03.678 0 7.225 0 7.225h2.4z"/>
                </svg>
              </SocialIcon>
            </FooterSocial>
          </FooterAbout>
          
          <FooterColumn className="footer-column">
            <h3>Unsere Services</h3>
            <ul>
              <li><Link to="/sicherheit">Sicherheitsdienste</Link></li>
              <li><Link to="/taxi">Taxi&Mietwagenservice</Link></li>
              <li><Link to="/reinigung">Reinigungsdienste</Link></li>
              <li><Link to="/jobs">Karriere bei uns</Link></li>
              <li><Link to="/#ueber-uns">Über GuardiansMoves</Link></li>
            </ul>
          </FooterColumn>
          
          <FooterColumn className="footer-column">
  <h3>Kontakt</h3>
  <FooterContactItem className="footer-contact-item">
    <FooterContactIcon className="footer-contact-icon">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
        <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
      </svg>
    </FooterContactIcon>
    <FooterContactText className="footer-contact-text">
      <a 
        href="https://maps.google.com/?q=Müllerstr.+3,+13353+Berlin,+Germany" 
        target="_blank" 
        rel="noopener noreferrer"
        className="footer-contact-link"
      >
        Müllerstr. 3<br />
        13353 Berlin
      </a>
    </FooterContactText>
  </FooterContactItem>
  
  <FooterContactItem className="footer-contact-item">
    <FooterContactIcon className="footer-contact-icon">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
        <path d="M3.654 1.328a.678.678 0 0 0-1.015-.063L1.605 2.3c-.483.484-.661 1.169-.45 1.77a17.568 17.568 0 0 0 4.168 6.608 17.569 17.569 0 0 0 6.608 4.168c.601.211 1.286.033 1.77-.45l1.034-1.034a.678.678 0 0 0-.063-1.015l-2.307-1.794a.678.678 0 0 0-.58-.122l-2.19.547a1.745 1.745 0 0 1-1.657-.459L5.482 8.062a1.745 1.745 0 0 1-.46-1.657l.548-2.19a.678.678 0 0 0-.122-.58L3.654 1.328zM1.884.511a1.745 1.745 0 0 1 2.612.163L6.29 2.98c.329.423.445.974.315 1.494l-.547 2.19a.678.678 0 0 0 .178.643l2.457 2.457a.678.678 0 0 0 .644.178l2.189-.547a1.745 1.745 0 0 1 1.494.315l2.306 1.794c.829.645.905 1.87.163 2.611l-1.034 1.034c-.74.74-1.846 1.065-2.877.702a18.634 18.634 0 0 1-7.01-4.42 18.634 18.634 0 0 1-4.42-7.009c-.362-1.03-.037-2.137.703-2.877L1.885.511z"/>
      </svg>
    </FooterContactIcon>
    <FooterContactText className="footer-contact-text">
      <a href="tel:+4915563547378" className="footer-contact-link">+49 155 6354 7378</a>
    </FooterContactText>
  </FooterContactItem>
  
  <FooterContactItem className="footer-contact-item">
    <FooterContactIcon className="footer-contact-icon">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
        <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2Zm13 2.383-4.708 2.825L15 11.105V5.383Zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741ZM1 11.105l4.708-2.897L1 5.383v5.722Z"/>
      </svg>
    </FooterContactIcon>
    <FooterContactText className="footer-contact-text">
      <a href="mailto:info@guardiansmoves.de" className="footer-contact-link">info@guardiansmoves.de</a>
    </FooterContactText>
  </FooterContactItem>
</FooterColumn>
          
          <FooterColumn className="footer-column">
            <h3>Öffnungszeiten</h3>
            <FooterContactItem className="footer-contact-item">
              <FooterContactIcon className="footer-contact-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M8.5 5.5a.5.5 0 0 0-1 0v3.362l-1.429 2.38a.5.5 0 1 0 .858.515l1.5-2.5A.5.5 0 0 0 8.5 9V5.5z"/>
                  <path d="M6.5 0a.5.5 0 0 0 0 1H7v1.07a7.001 7.001 0 0 0-3.273 12.474l-.602.602a.5.5 0 0 0 .707.708l.746-.746A6.97 6.97 0 0 0 8 16a6.97 6.97 0 0 0 3.422-.892l.746.746a.5.5 0 0 0 .707-.708l-.601-.602A7.001 7.001 0 0 0 9 2.07V1h.5a.5.5 0 0 0 0-1h-3zm1.038 3.018a6.093 6.093 0 0 1 .924 0 6 6 0 1 1-.924 0zM0 8.5A.5.5 0 0 1 .5 8h7a.5.5 0 0 1 0 1h-7A.5.5 0 0 1 0 8.5zm8-4a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5z"/>
                </svg>
              </FooterContactIcon>
              <FooterContactText className="footer-contact-text">
                Montag - Freitag: 8:00 - 18:00 Uhr<br />
                Samstag: 9:00 - 14:00 Uhr<br />
                Taxi&Mietwagenservice: 24/7
              </FooterContactText>
            </FooterContactItem>
          </FooterColumn>
        </FooterContent>
        
        <FooterBottom className="footer-bottom">
          <Copyright className="copyright">
            &copy; {new Date().getFullYear()} GuardiansMoves. Alle Rechte vorbehalten.
          </Copyright>
          <FooterBottomLinks className="footer-bottom-links">
            <Link to="/impressum">Impressum</Link>
            <Link to="/datenschutz">Datenschutzerklärung</Link>
          </FooterBottomLinks>
        </FooterBottom>
      </div>
    </FooterContainer>
  );
}

export default Footer;